import * as React from "react";
import color from "color";

import {
    UserProfileImageContainer,
    UserProfileUploadLabel,
    UserProfileUploadIcon,
    UserProfileInteriorProgress,
    UserProfileExteriorProgress,
    UserProfileCancelContainer,
} from "./user-profile-styles";
import { uploadCancelX } from "common/named-icons-lite";

interface Props {
    uploadProgress: number | undefined;
    onInputChange: ((ev: React.ChangeEvent<HTMLInputElement>) => void) | undefined;
    baseUploadColor: string;
    secondaryUploadColor: string;
    image: React.ReactNode;
    onCancelClick: (ev: React.MouseEvent<HTMLElement>) => void;
}

let count = 0;

const UploadButton = (props: { fillColor: string; interiorColor: string }) => (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M18 36C8.05887 36 0 27.9411 0 18C0 8.05887 8.05887 0 18 0C27.9411 0 36 8.05887 36 18C36 27.9411 27.9411 36 18 36Z"
            fill={props.interiorColor}
        />
        <path
            d="M18 2C9.164 2 2 9.164 2 18C2 26.836 9.164 34 18 34C26.836 34 34 26.836 34 18C33.9907 9.168 26.832 2.00933 18 2ZM25.3333 24H10.6667C9.19333 24 8 22.8067 8 21.3333V14.6667C8 13.1933 9.19333 12 10.6667 12H12.6667C12.912 11.9773 13.144 11.8813 13.3333 11.724L15.0573 10C15.496 9.592 16.068 9.35467 16.6667 9.33333H19.3333C19.932 9.35467 20.504 9.592 20.9427 10L22.6667 11.724C22.856 11.8813 23.088 11.9773 23.3333 12H25.3333C26.8067 12 28 13.1933 28 14.6667V21.3333C28 22.8067 26.8067 24 25.3333 24ZM18 14C20.2093 14 22 15.7907 22 18C22 20.2093 20.2093 22 18 22C15.7907 22 14 20.2093 14 18C14 15.7907 15.7907 14 18 14Z"
            fill={props.fillColor}
        />
    </svg>
);

export function UserProfileImage(props: Props): JSX.Element {
    const { uploadProgress, onInputChange, onCancelClick, baseUploadColor, secondaryUploadColor, image } = props;
    const id = `inline-image-upload-${count++}`;

    const progressForColoring = uploadProgress ?? 0;
    const colorInfo = color(baseUploadColor);
    const progressColor = (colorInfo.isDark() ? colorInfo : colorInfo.darken(0.5)).rgb().hex();
    const antiProgressColor = (colorInfo.isDark() ? colorInfo.lighten(0.5) : colorInfo).rgb().hex();

    const topRight = progressColor;
    const bottomRight = progressForColoring < 50 ? antiProgressColor : progressColor;
    const bottomLeft = progressForColoring < 75 ? antiProgressColor : progressColor;
    const topLeft = progressForColoring < 100 ? antiProgressColor : progressColor;

    const progressTopColor = progressForColoring < 25 ? antiProgressColor : progressColor;
    const rotationTop = (progressForColoring < 25 ? progressForColoring : progressForColoring - 25) / 100;
    const fillColor = colorInfo.rgb().hex();

    const uploadIconNode =
        uploadProgress === undefined ? (
            <UploadButton fillColor={fillColor} interiorColor={secondaryUploadColor} />
        ) : (
            <UserProfileCancelContainer onClick={onCancelClick} bg={fillColor}>
                {uploadCancelX(secondaryUploadColor)}
            </UserProfileCancelContainer>
        );

    const inputRef = React.useRef<HTMLInputElement>(null);
    const lastUploadProgressRef = React.useRef<number | undefined>(undefined);
    React.useEffect(() => {
        const lastUploadProgress = lastUploadProgressRef.current;
        if (uploadProgress === undefined && lastUploadProgress !== undefined) {
            const { current } = inputRef;
            if (current === null) return;

            current.value = "";
        }
        lastUploadProgressRef.current = uploadProgress;
    }, [uploadProgress]);

    return (
        <UserProfileImageContainer>
            {onInputChange === undefined ? null : (
                <input
                    type="file"
                    id={id}
                    accept={"image/*"}
                    onChange={onInputChange}
                    disabled={uploadProgress !== undefined}
                    ref={inputRef}
                />
            )}
            <UserProfileUploadLabel htmlFor={id}>
                {image}
                {onInputChange === undefined ? null : <UserProfileUploadIcon>{uploadIconNode}</UserProfileUploadIcon>}
                {uploadProgress === undefined ? null : (
                    <UserProfileInteriorProgress tl={topLeft} bl={bottomLeft} br={bottomRight} tr={topRight} />
                )}
                {uploadProgress === undefined ? null : (
                    <UserProfileExteriorProgress bgColor={progressTopColor} rotationOffset={rotationTop} />
                )}
            </UserProfileUploadLabel>
        </UserProfileImageContainer>
    );
}
